<!-- #################################################################################### -->
<!-- ###### HERINCOHAN                                                             ###### -->
<!-- ###### @author: Cristian Giraldo Villegas                                     ###### -->
<!-- ###### @version Agosto 2026                                                   ###### -->
<!-- #################################################################################### -->

<!-- #################################################################################### -->
<!-- ###### Sección de HTML                                                        ###### -->
<!-- #################################################################################### -->

<template>
  <v-container fluid>
    <v-snackbar
      v-model="snackbar"
      :timeout="timeout"
      top
      :color="alertData[1] ? 'green darken-2' : 'red darken-2'"
      class="text-center"
    >
      {{ alertData[0] }}
    </v-snackbar>
    <v-card class="pa-4 pt-1"> 
      <div>
        <h3 class="text-body">
          Carga masiva de Radicación
        </h3>
      </div>

      <!-- Texto informativo -->
      <div class="text-body">
        Este módulo permite cargar un archivo en formato <strong>.csv</strong> con la
        información necesaria para relacionar un número de radicado con una factura.
        Tenga en cuenta que solo se guardarán los registros que no presenten novedades.
        <br>
        Cada archivo puede contener un máximo de {{ cantidadMaximaPermitida }} facturas.
        Al confirmar la carga, deberá indicar un correo electrónico y, una vez finalice el
        proceso, recibirá en ese correo una notificación junto con el listado de novedades
        encontradas para cada registro que no pudo ser cargado.
        <br> Formato del archivo esperado: <br>
        <ul>
          <li> PREFIJO FACTURA | NÚMERO FACTURA | NÚMERO RADICADO | FECHA RADICACIÓN (DD/MM/YYYY H:MM) | OBSERVACIONES (OPCIONAL) </li>
        </ul>
      </div>

      <v-row class="mt-2">
        <v-col cols="12" class="input-values">
          <!-- Boton de seleccion de archivo a subir -->
          <div class="archivo">
            <input 
              :disabled="isButtonSelectFileDisabled"
              type="file" 
              class="seleccionarArchivo" 
              accept=".csv"
              @change="handleFileChange"
              @click="e => (e.target.value = null)"
              ref="fileInput"
            >
            <p v-if="isButtonSelectFileDisabled" style="padding-top: 0.6rem;">
              Validando la información del archivo, espere un momento...
              <v-icon class="rotate-animation" color="#0d47a1" large>rotate_right</v-icon>
            </p>
            <p v-else-if="file" style="padding-top: 0.6rem;">
              {{ file.name }}
            </p>
            <p v-else>Arrastre un archivo .CSV <br> También puede hacer click en este cuadro. </p>
          </div>
        </v-col>
      </v-row>

      <!-- Boton para abrir el modal de validacion de carga de archivos -->
      <v-row>
        <v-spacer></v-spacer>
        <v-col cols="12" md="4" class="input-values" v-if="showCargarMasivaButton">
          <v-btn 
            color="success" 
            @click="handleChooseFunction"
            :loading="isVerifingInformacionCargada"
          >
            Validar datos cargados
          </v-btn>
        </v-col>
      </v-row>

      <!-- Modal para confirmar la programacion de la carga de los archivos ingresados -->
       <v-dialog v-model="modalAgendarCarga" max-width="620" width="100%" persistent>
        <v-card>
          <v-card-title class="justify-center text-center primary white--text pt-2 pb-2">
            Cargar datos
          </v-card-title>
          <!-- campo para ingresar el correo al cual llegara la notificacion junto el listado de novedades-->
          <v-text-field
            ref="campoCorreo"
            :rules="rules.email"
            class="ml-4 mr-4 mt-4"
            label="Correo electronico*"
            dense
            outlined
            v-model="correoElectronicoIngresado"
            type="email"
          >
            <template v-slot:prepend-inner>
              <v-icon>mail</v-icon>
            </template>
          </v-text-field>
          <!-- Botones de acción dentro del modal -->
          <v-card-actions class="mt-0 pt-0">
            <v-spacer></v-spacer>
            <!-- Boton para cancelar la carga del archivo -->
            <v-btn color="error" text @click="modalAgendarCarga = false; limpiarCampos();" :disabled="isButtonUploadFileDisabled">
              No
            </v-btn>
            <!-- Botón para guardar la radiacion de la factura -->
            <v-btn
              color="success" 
              :loading="isUploadingFile"
              :disabled="isButtonUploadFileDisabled || isDisabledButtonConfirmarDescargaByParameters"
              @click="handleScheduleCargaMasiva"
            >
              Confirmar carga masiva
            </v-btn>
          </v-card-actions>
        </v-card>
       </v-dialog>
    </v-card>
  </v-container>
</template>

<!-- #################################################################################### -->
<!-- ###### Sección de Scripts                                                     ###### -->
<!-- #################################################################################### -->

<script>
  import { mapState } from "vuex";
  export default {
  data() {
    return {
        rules: {
        // regla para validar que el texto ingresado tenga la estructura de un correo
        email: [
          (email) => {
            if(!email){
              return "Campo obligatorio.";
            }

            if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)){
              return "Ingrese un correo válido";
            }

            return true;
          }
        ],
      },
      // variable que indica si el boton de seleccionar archivo esta deshabilitado
      isButtonSelectFileDisabled: false,
      // variable que indica si se muestra o no el boton de descargar expedientes
      showCargarMasivaButton: false,
      // Archivo cargado
      file: null,
      // Variable que indica si se esta validando la informacion cargada en busca de novedades
      isVerifingInformacionCargada: false,
      snackbar: false,
      alertData: ["", false],
      timeout: 4000,
      // Variable que almacena la cantidad maxima de facturas que se pueden radicar en una sola carga
      cantidadMaximaPermitida: 4000,
      // Variable que controla la visibilidad del modal de agendamiento de la carga masiva
      modalAgendarCarga: false,
      isUploadingFile: false,
      isButtonUploadFileDisabled: false,
      // Variable que almacena el correo electronico al cual se le enviara la notificacion
      correoElectronicoIngresado: null,
    }
  },

  computed: {
    ...mapState(["auth", "enterprise"]),

    isDisabledButtonConfirmarDescargaByParameters(){
      if(!this.correoElectronicoIngresado || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.correoElectronicoIngresado)){
        return true;
      } else {
        return false;
      }  
    }
  },

  methods: {
    /* Método para mostrar notificaciones en el snackbar */
    handleAlert(newValue) {
      this.alertData = newValue;
      this.snackbar = true;
    },

    // Se limpia el valor del archivo y se fuerza al navegador a detectar la proxima seleccion del mismo
    resetInput() {
      this.file = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    // Metodo que se encarga de gestionar la visibilidad del boton que permite la 
    // visualizacion del boton de edicion de estados
    handleShowCargarMasivaButton(nuevoValor){
      this.showCargarMasivaButton = nuevoValor;
    },

    // Metodo encargado de manejar el cambio de archivo seleccionado junto con
    // la subida del archivo al backend y que retorna un archivo .csv
    // con el reporte de la validacion de los registros ingresados dentro del archivo
    async handleFileChange(event){
      try{
        this.isButtonSelectFileDisabled = true;
        this.handleShowCargarMasivaButton(false);

        const selectedFile = (event.target.files && event.target.files[0]) || null;

        if (!selectedFile) return;

        if (!/\.(xlsx|csv)$/i.test(selectedFile.name)) {
          this.handleAlert([
          "El archivo debe ser de tipo .csv",
          false,
          ]);
          this.resetInput();
          return;
        }
        const maxSizeMB = 10485760;
        if (selectedFile.size > maxSizeMB) {
          this.handleAlert([`El archivo supera el tamaño máximo permitido de 10 MB`, false]);
          this.resetInput();
          return;
        }
        this.file = selectedFile;
        this.handleShowCargarMasivaButton(true);
      } catch (error) {
        console.log(error);
        this.resetInput();
        this.handleAlert(["Error al leer el archivo", false]);
        this.handleShowCargarMasivaButton(false);
      } finally {
        this.isButtonSelectFileDisabled = false
      }
    },

    // Metodo para contar la cantidad de registros que contiene el archivo cargado
    async contarFilas(file) {
      const texto = await file.text();
      let filas = 0;
      let hayContenido = false;
      for (let i = 0; i < texto.length; i++) {
        const c = texto[i];
        if (c === "\n" || c === "\r") {
          if (hayContenido) filas++;
          hayContenido = false;
          if (c === "\r" && texto[i + 1] === "\n") i++;
        } else {
          hayContenido = true;
        }
      }
      if (hayContenido) filas++;
      return Math.max(0, filas - 1);
    },

    // Metodo encargado de elegir si la carga se realizara delegandole la responsabilidad a una tarea
    // en msa-tasks o si se realizara en el momento con msa-invoicing
    async handleChooseFunction(){
      this.isVerifingInformacionCargada = true;
      const total = await this.contarFilas(this.file);
      if(total > this.cantidadMaximaPermitida){
        this.handleAlert([`La cantidad facturas ingresada supera el limite maximo permitido de ${this.cantidadMaximaPermitida}`, false]);
      } else {
        this.modalAgendarCarga = true;
      }
      this.isVerifingInformacionCargada = false;
    },

    // Metodo encargado de almacenar los valores de todas las facturas a radicar para 
    // programar su respectiva carga
    async handleScheduleCargaMasiva(){
      try{
        this.isUploadingFile = true;
        this.isButtonUploadFileDisabled = true;

        const formData = new FormData();
        formData.append("archivo", this.file, this.file.name);

        const response = await this.$http.post(`msa-invoicing/api/radicacion-entrega/carga/archivo`, 
          formData, 
          { 
            headers: { "Content-Type": "multipart/form-data"},
            params: {
              idEmpresa: this.enterprise.code,
              correoIngresado: this.correoElectronicoIngresado,
              usuario: this.auth.username
            },
          },
        );
        if(response && response.status == 200){
          this.handleShowCargarMasivaButton(false);
          this.limpiarCampos();
          this.resetInput();
          this.handleAlert([`Se ha agendado la radicación de facturas con exito. Cuando este disponible le llegara un mensaje al correo electronico ingresado`, true]);
        }
      } catch(e){
        console.log(e);
      } finally {
        this.isUploadingFile = false;
        this.isButtonUploadFileDisabled = false;
        this.modalAgendarCarga = false;
      }
    },

    // metodo encargado de limpiar los campos
    limpiarCampos(){
      this.correoElectronicoIngresado = null;
      if (this.$refs.campoCorreo) {
        this.$refs.campoCorreo.resetValidation();
      }
    },
  }
}

</script>

<!-- #################################################################################### -->
<!-- ###### Sección de Styles                                                      ###### -->
<!-- #################################################################################### -->

<style scoped>

.input-values {
  padding-bottom: 10px;
  padding-top: 10px;
}

.button-select-file{
  display: none
}

.text-body {
  margin-left: 1.2rem
}

.seleccionarArchivo {
  opacity: 0;
  width: 100%;
  height: 100px;
  position: absolute;
  cursor: pointer;
}

.seleccionarArchivo:disabled {
  cursor: default;
}

.archivo {
    outline: 2px dashed grey;
    outline-offset: -10px;
    background: lightcyan;
    color: dimgray;
    padding: 1rem;
    height: 100px;
    position: relative;
    cursor: pointer;
}
.archivo p {
    margin-top: .7rem;
    text-align: center;
}

.rotate-animation {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
from {
  transform: rotate(0deg);
}

to {
  transform: rotate(360deg);
}
}

.scroll-table-body {
  display: block;
  max-height: 300px; 
  overflow-y: auto;
}

.scroll-table-body tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}

.scroll-table-body td {
  white-space: normal;
  word-break: break-word;
}

.sin-novedades {
  text-align: center;
  font-weight: 600;
  color: #4caf50;
  padding: 12px;
}

.error-message {
  color: red;
}

</style>